'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  BarChart, 
  TrendingUp, 
  Package, 
  DollarSign, 
  Users, 
  Star, 
  Plus,
  Edit,
  Eye,
  Calendar,
  MapPin,
  Leaf,
  AlertCircle
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';

// 농가 판매 데이터 (목업)
const salesData = {
  totalRevenue: 2450000,
  monthlyRevenue: 540000,
  totalProducts: 12,
  activeProducts: 8,
  totalOrders: 156,
  monthlyOrders: 23,
  avgRating: 4.8,
  totalReviews: 89
};

const recentOrders = [
  {
    id: 'ORD-001',
    customer: '김고객',
    product: '평창 감자',
    quantity: '5kg',
    amount: 15000,
    status: 'completed',
    date: '2025-01-10'
  },
  {
    id: 'ORD-002', 
    customer: '박구매',
    product: '대관령 옥수수',
    quantity: '10개',
    amount: 12000,
    status: 'shipping',
    date: '2025-01-09'
  },
  {
    id: 'ORD-003',
    customer: '이농산',
    product: '평창 감자',
    quantity: '3kg',
    amount: 9000,
    status: 'pending',
    date: '2025-01-08'
  },
  {
    id: 'ORD-004',
    customer: '최소비',
    product: '대관령 옥수수',
    quantity: '5개',
    amount: 6000,
    status: 'completed',
    date: '2025-01-07'
  }
];

const products = [
  {
    id: 1,
    name: '평창 감자',
    price: 15000,
    unit: '5kg',
    stock: 50,
    sold: 23,
    rating: 4.9,
    reviews: 15,
    status: 'active',
    image: '🥔'
  },
  {
    id: 2,
    name: '대관령 옥수수',
    price: 12000,
    unit: '10개',
    stock: 30,
    sold: 18,
    rating: 4.7,
    reviews: 12,
    status: 'active',
    image: '🌽'
  },
  {
    id: 3,
    name: '평창 배추',
    price: 8000,
    unit: '1포기',
    stock: 0,
    sold: 45,
    rating: 4.8,
    reviews: 22,
    status: 'out_of_stock',
    image: '🥬'
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-800 border-green-200';
    case 'shipping': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'completed': return '배송완료';
    case 'shipping': return '배송중';
    case 'pending': return '준비중';
    default: return '알수없음';
  }
};

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">농가 대시보드</h1>
              <p className="text-gray-600 mt-1">평창 행복농장 - 박농부님</p>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/dashboard/story-edit">
                <Button className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white">
                  <Edit className="w-4 h-4 mr-2" />
                  농가 스토리 수정
                </Button>
              </Link>
              <Button variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                상품 등록
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: '개요', icon: BarChart },
              { id: 'orders', label: '주문관리', icon: Package },
              { id: 'products', label: '상품관리', icon: Leaf }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">총 매출</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {salesData.totalRevenue.toLocaleString()}원
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">이번 달 매출</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {salesData.monthlyRevenue.toLocaleString()}원
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Package className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">총 주문</p>
                      <p className="text-2xl font-bold text-gray-900">{salesData.totalOrders}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Star className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">평점</p>
                      <p className="text-2xl font-bold text-gray-900">{salesData.avgRating}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sales Trend Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    월별 매출 추이
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end justify-between gap-2 px-4 py-8">
                    {[
                      { month: '8월', value: 180000, height: '45%' },
                      { month: '9월', value: 220000, height: '55%' },
                      { month: '10월', value: 280000, height: '70%' },
                      { month: '11월', value: 350000, height: '87%' },
                      { month: '12월', value: 420000, height: '100%' },
                      { month: '1월', value: 540000, height: '100%' }
                    ].map((item, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center gap-2">
                        <div className="text-xs text-gray-600 font-medium">
                          {item.value.toLocaleString()}
                        </div>
                        <div 
                          className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t-md transition-all duration-500 hover:from-green-600 hover:to-green-500 relative group cursor-pointer"
                          style={{ height: item.height }}
                        >
                          <div className="absolute inset-0 bg-white bg-opacity-20 rounded-t-md group-hover:bg-opacity-30 transition-all"></div>
                        </div>
                        <div className="text-xs text-gray-500 font-medium">{item.month}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-center text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span>월별 매출액</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Product Performance Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart className="w-5 h-5" />
                    상품별 판매량
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6 py-4">
                    {[
                      { name: '평창 감자', value: 45, color: 'bg-blue-500', percentage: 100 },
                      { name: '대관령 옥수수', value: 38, color: 'bg-green-500', percentage: 84 },
                      { name: '평창 배추', value: 32, color: 'bg-yellow-500', percentage: 71 },
                      { name: '강릉 콩', value: 28, color: 'bg-purple-500', percentage: 62 },
                      { name: '횡성 한우', value: 15, color: 'bg-red-500', percentage: 33 }
                    ].map((item, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">{item.name}</span>
                          <span className="text-sm text-gray-600">{item.value}개</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`${item.color} h-2.5 rounded-full transition-all duration-700 ease-out`}
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  최근 주문
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">주문번호</th>
                        <th className="text-left py-3 px-2">고객명</th>
                        <th className="text-left py-3 px-2">상품</th>
                        <th className="text-left py-3 px-2">수량</th>
                        <th className="text-left py-3 px-2">금액</th>
                        <th className="text-left py-3 px-2">상태</th>
                        <th className="text-left py-3 px-2">주문일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id} className="border-b">
                          <td className="py-3 px-2 font-mono text-sm">{order.id}</td>
                          <td className="py-3 px-2">{order.customer}</td>
                          <td className="py-3 px-2">{order.product}</td>
                          <td className="py-3 px-2">{order.quantity}</td>
                          <td className="py-3 px-2 font-medium">{order.amount.toLocaleString()}원</td>
                          <td className="py-3 px-2">
                            <Badge className={getStatusColor(order.status)}>
                              {getStatusText(order.status)}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-sm text-gray-600">{order.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  주문 관리
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentOrders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="font-mono text-sm font-medium">{order.id}</div>
                          <Badge className={getStatusColor(order.status)}>
                            {getStatusText(order.status)}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-500">{order.date}</div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">고객: </span>
                          <span className="font-medium">{order.customer}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">상품: </span>
                          <span className="font-medium">{order.product}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">수량: </span>
                          <span className="font-medium">{order.quantity}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-lg font-bold text-green-600">
                          {order.amount.toLocaleString()}원
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-1" />
                            상세보기
                          </Button>
                          {order.status === 'pending' && (
                            <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white">
                              발송하기
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Leaf className="w-5 h-5" />
                    상품 관리
                  </CardTitle>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    새 상품 등록
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product) => (
                    <Card key={product.id} className="overflow-hidden">
                      <div className="aspect-video bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center text-4xl relative">
                        {product.image}
                        {product.status === 'out_of_stock' && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <Badge className="bg-red-500 text-white">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              품절
                            </Badge>
                          </div>
                        )}
                      </div>
                      
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-lg">{product.name}</h3>
                          <div className="flex items-center text-sm text-yellow-600">
                            <Star className="w-4 h-4 fill-current mr-1" />
                            {product.rating}
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <div className="flex justify-between">
                            <span>가격</span>
                            <span className="font-medium">{product.price.toLocaleString()}원/{product.unit}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>재고</span>
                            <span className={`font-medium ${product.stock === 0 ? 'text-red-600' : ''}`}>
                              {product.stock}개
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>총 판매</span>
                            <span className="font-medium">{product.sold}개</span>
                          </div>
                          <div className="flex justify-between">
                            <span>리뷰</span>
                            <span className="font-medium">{product.reviews}개</span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="flex-1">
                            <Edit className="w-4 h-4 mr-1" />
                            수정
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1">
                            <Eye className="w-4 h-4 mr-1" />
                            보기
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}